// The slice of OpenCV.js this file calls. The package's own typings need a newer TypeScript than 3.9.
export interface Mat {
  rows: number
  cols: number
  data: Uint8Array
  data32S: Int32Array
  type(): number
  delete(): void
}

export interface MatVector {
  size(): number
  get(index: number): Mat
  delete(): void
}

interface MatConstructor {
  new (): Mat
  new (rows: number, cols: number, type: number, scalar: number[]): Mat
  ones(rows: number, cols: number, type: number): Mat
}

export interface Cv {
  Mat: MatConstructor
  MatVector: new () => MatVector
  Size: new (width: number, height: number) => object
  Point: new (x: number, y: number) => object
  Scalar: new () => object
  CV_8U: number
  CV_32FC2: number
  COLOR_RGBA2GRAY: number
  COLOR_RGBA2RGB: number
  COLOR_RGB2HSV: number
  RETR_LIST: number
  CHAIN_APPROX_SIMPLE: number
  MORPH_OPEN: number
  MORPH_CLOSE: number
  INTER_LINEAR: number
  BORDER_DEFAULT: number
  BORDER_CONSTANT: number
  BORDER_REPLICATE: number
  cvtColor(src: Mat, dst: Mat, code: number): void
  GaussianBlur(src: Mat, dst: Mat, size: object, sigmaX: number, sigmaY: number, borderType: number): void
  Canny(src: Mat, dst: Mat, threshold1: number, threshold2: number, apertureSize: number, l2gradient: boolean): void
  dilate(src: Mat, dst: Mat, kernel: Mat, anchor: object, iterations: number, borderType: number, borderValue: object): void
  morphologyEx(src: Mat, dst: Mat, operation: number, kernel: Mat): void
  inRange(src: Mat, lower: Mat, upper: Mat, dst: Mat): void
  split(src: Mat, channels: MatVector): void
  max(a: Mat, b: Mat, dst: Mat): void
  findContours(image: Mat, contours: MatVector, hierarchy: Mat, mode: number, method: number): void
  contourArea(contour: Mat): number
  arcLength(curve: Mat, closed: boolean): number
  approxPolyDP(curve: Mat, approx: Mat, epsilon: number, closed: boolean): void
  isContourConvex(contour: Mat): boolean
  convexHull(points: Mat, hull: Mat, clockwise: boolean, returnPoints: boolean): void
  matFromImageData(imageData: ImageData): Mat
  matFromArray(rows: number, cols: number, type: number, values: number[]): Mat
  getPerspectiveTransform(src: Mat, dst: Mat): Mat
  warpPerspective(src: Mat, dst: Mat, transform: Mat, size: object, flags: number, borderMode: number, borderValue: object): void
  morphologyDefaultBorderValue(): object
  // emscripten module hooks used by loadOpenCv
  then?: (onReady: (module: Cv) => void, onError: () => void) => void
  onRuntimeInitialized?: () => void
}

export interface Pt {
  x: number
  y: number
}

interface Deletable {
  delete(): void
}

// a sheet should take a decent part of the view: measured against the square on the frame's shorter side, so the
// tall portrait frame of the full-screen preview does not demand a bigger sheet than a squarish one
const MIN_AREA_RATIO = 0.2
// a quad filling the frame is the border or a bezel, not a sheet of paper, and would not need a warp anyway
const MAX_AREA_RATIO = 0.85
// a sheet with a finger over it still fills most of its hull
const MIN_SOLIDITY = 0.8
// corners this close to the frame edge belong to the border; a sheet may touch it with two at most
const EDGE_PX = 3
const MAX_EDGE_CORNERS = 2
// plausible sheet shape: warped width/height ratio and shortest side relative to the frame width
const MIN_ASPECT = 0.4
const MAX_ASPECT = 2.5
const MIN_SIDE_RATIO = 0.25
// bridges dark bands (table headers, title bars) that would cut the paper blob into partial quads
const PAPER_CLOSE_PX = 7
// seals breaks up to this size in the sheet outline (a thumb over the edge, a faint stretch) without thickening it
const EDGE_CLOSE_PX = 5
// a warp output shorter than this share of the frame's shorter side would lose the page
const MIN_WARP_RATIO = 0.5

let opencv: Promise<Cv | null> | null = null

// Resolves with the ready OpenCV module, or null when it cannot be loaded (the feature then stays off).
// The 10 MB module is its own lazy chunk, so only the OCR screen pays for it.
export function loadOpenCv(): Promise<Cv | null> {
  if (opencv) return opencv
  opencv = import(/* webpackChunkName: "opencv" */ '@/utils/opencvModule')
    .then(
      ({ default: cv }) =>
        new Promise<Cv | null>((resolve) => {
          // a resolved value that still has a `then` would be awaited forever
          const ready = (module: Cv) => {
            module.then = undefined
            resolve(module)
          }
          if (typeof cv.then === 'function') cv.then((module: Cv) => ready(module || cv), () => resolve(null))
          else if (cv.Mat) ready(cv)
          else cv.onRuntimeInitialized = () => ready(cv)
        })
    )
    .catch(() => null)
    .then((cv) => {
      if (!cv) opencv = null
      return cv
    })
  return opencv
}

// every Mat/MatVector registered through `own` is deleted when the callback ends or throws
function scoped<T>(run: (own: <M extends Deletable>(mat: M) => M) => T): T {
  const owned: Deletable[] = []
  try {
    return run((mat) => {
      owned.push(mat)
      return mat
    })
  } finally {
    owned.forEach((mat) => mat.delete())
  }
}

function polygonArea(pts: Pt[]) {
  let sum = 0
  pts.forEach((p, i) => {
    const q = pts[(i + 1) % pts.length]
    sum += p.x * q.y - q.x * p.y
  })
  return Math.abs(sum) / 2
}

const distance = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y)

// clockwise on screen, starting from the corner closest to the top-left: tl, tr, br, bl
function orderQuad(pts: Pt[]): Pt[] {
  const cx = pts.reduce((sum, p) => sum + p.x, 0) / pts.length
  const cy = pts.reduce((sum, p) => sum + p.y, 0) / pts.length
  const clockwise = [...pts].sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx))
  let first = 0
  clockwise.forEach((p, i) => {
    if (p.x + p.y < clockwise[first].x + clockwise[first].y) first = i
  })
  return clockwise.slice(first).concat(clockwise.slice(0, first))
}

// warped output size of an ordered quad
function warpSize(quad: Pt[]) {
  const [tl, tr, br, bl] = quad
  return {
    width: Math.max(distance(tl, tr), distance(bl, br)),
    height: Math.max(distance(tl, bl), distance(tr, br)),
  }
}

function isPlausible(quad: Pt[], cols: number, rows: number): boolean {
  const { width, height } = warpSize(quad)
  const aspect = width / height
  const atEdge = quad.filter((p) => p.x <= EDGE_PX || p.y <= EDGE_PX || p.x >= cols - 1 - EDGE_PX || p.y >= rows - 1 - EDGE_PX)
  return (
    atEdge.length <= MAX_EDGE_CORNERS &&
    aspect >= MIN_ASPECT &&
    aspect <= MAX_ASPECT &&
    Math.min(width, height) >= cols * MIN_SIDE_RATIO
  )
}

// `hull` closes concave bites (a finger over the sheet) before looking for the 4 corners
function largestQuad(cv: Cv, binary: Mat, hull: boolean): Pt[] | null {
  return scoped((own) => {
    const frameArea = binary.rows * binary.cols
    const viewArea = binary.cols * Math.min(binary.rows, binary.cols)
    const contours = own(new cv.MatVector())
    const hierarchy = own(new cv.Mat())
    cv.findContours(binary, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE)
    let best: Pt[] | null = null
    let bestArea = viewArea * MIN_AREA_RATIO
    for (let i = 0; i < contours.size(); i++) {
      const contour = contours.get(i)
      const outline = new cv.Mat()
      const approx = new cv.Mat()
      try {
        if (hull) cv.convexHull(contour, outline, false, true)
        const shape = hull ? outline : contour
        const shapeArea = cv.contourArea(shape)
        if (shapeArea < bestArea || (hull && cv.contourArea(contour) < shapeArea * MIN_SOLIDITY)) continue
        cv.approxPolyDP(shape, approx, 0.02 * cv.arcLength(shape, true), true)
        if (approx.rows !== 4 || !cv.isContourConvex(approx)) continue
        const pts: Pt[] = []
        for (let k = 0; k < 4; k++) pts.push({ x: approx.data32S[k * 2], y: approx.data32S[k * 2 + 1] })
        const area = polygonArea(pts)
        const quad = orderQuad(pts)
        if (area > bestArea && area <= frameArea * MAX_AREA_RATIO && isPlausible(quad, binary.cols, binary.rows)) {
          best = quad
          bestArea = area
        }
      } finally {
        contour.delete()
        outline.delete()
        approx.delete()
      }
    }
    return best
  })
}

// Canny on a lightly blurred single channel
function cannyOf(cv: Cv, channel: Mat, edges: Mat) {
  const blur = new cv.Mat()
  try {
    cv.GaussianBlur(channel, blur, new cv.Size(5, 5), 0, 0, cv.BORDER_DEFAULT)
    cv.Canny(blur, edges, 50, 150, 3, false)
  } finally {
    blur.delete()
  }
}

function quadFromEdges(cv: Cv, rgba: Mat): Pt[] | null {
  return scoped((own) => {
    const gray = own(new cv.Mat())
    const rgb = own(new cv.Mat())
    const hsv = own(new cv.Mat())
    const channels = own(new cv.MatVector())
    const edges = own(new cv.Mat())
    const colorEdges = own(new cv.Mat())
    const thick = own(new cv.Mat())
    const kernel = own(cv.Mat.ones(3, 3, cv.CV_8U))
    const seal = own(cv.Mat.ones(EDGE_CLOSE_PX, EDGE_CLOSE_PX, cv.CV_8U))
    cv.cvtColor(rgba, gray, cv.COLOR_RGBA2GRAY)
    cv.cvtColor(rgba, rgb, cv.COLOR_RGBA2RGB)
    cv.cvtColor(rgb, hsv, cv.COLOR_RGB2HSV)
    cv.split(hsv, channels)
    const saturation = channels.get(1)
    try {
      cannyOf(cv, gray, edges)
      // paper under warm light can differ from its background in colour only (tan sheet, grey monitor): no luminance edge
      cannyOf(cv, saturation, colorEdges)
    } finally {
      saturation.delete()
    }
    cv.max(edges, colorEdges, edges) // union of two 0/255 edge maps
    cv.morphologyEx(edges, edges, cv.MORPH_CLOSE, seal)
    cv.dilate(edges, thick, kernel, new cv.Point(-1, -1), 1, cv.BORDER_CONSTANT, cv.morphologyDefaultBorderValue())
    return largestQuad(cv, thick, true)
  })
}

// pale paper on a saturated background: low saturation, high value
function quadFromPaperMask(cv: Cv, rgba: Mat): Pt[] | null {
  return scoped((own) => {
    const rgb = own(new cv.Mat())
    const hsv = own(new cv.Mat())
    cv.cvtColor(rgba, rgb, cv.COLOR_RGBA2RGB)
    cv.cvtColor(rgb, hsv, cv.COLOR_RGB2HSV)
    const low = own(new cv.Mat(hsv.rows, hsv.cols, hsv.type(), [0, 0, 140, 0]))
    const high = own(new cv.Mat(hsv.rows, hsv.cols, hsv.type(), [180, 70, 255, 255]))
    const mask = own(new cv.Mat())
    const kernel = own(cv.Mat.ones(5, 5, cv.CV_8U))
    const bridge = own(cv.Mat.ones(PAPER_CLOSE_PX, PAPER_CLOSE_PX, cv.CV_8U))
    cv.inRange(hsv, low, high, mask)
    cv.morphologyEx(mask, mask, cv.MORPH_OPEN, kernel)
    cv.morphologyEx(mask, mask, cv.MORPH_CLOSE, bridge)
    return largestQuad(cv, mask, true)
  })
}

// `imageData` is expected pre-downscaled to roughly 320 px wide
export function findDocumentQuad(cv: Cv, imageData: ImageData): Pt[] | null {
  return scoped((own) => {
    const rgba = own(cv.matFromImageData(imageData))
    const byEdges = quadFromEdges(cv, rgba)
    const byMask = quadFromPaperMask(cv, rgba)
    if (byEdges && byMask) return polygonArea(byMask) > polygonArea(byEdges) ? byMask : byEdges
    return byEdges || byMask
  })
}

// quad corners are in `source` pixels, ordered tl, tr, br, bl
export function warpDocument(cv: Cv, source: HTMLCanvasElement, quad: Pt[]): HTMLCanvasElement {
  const [tl, tr, br, bl] = quad
  const size = warpSize(quad)
  const width = Math.round(size.width)
  const height = Math.round(size.height)
  const sourceContext = source.getContext('2d')
  const out = document.createElement('canvas')
  const outContext = out.getContext('2d')
  const minSide = Math.min(source.width, source.height) * MIN_WARP_RATIO
  if (!sourceContext || !outContext || Math.min(width, height) < minSide) throw new Error('cannot warp document')
  out.width = width
  out.height = height
  return scoped((own) => {
    const src = own(cv.matFromImageData(sourceContext.getImageData(0, 0, source.width, source.height)))
    const from = own(cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y]))
    const to = own(cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, width - 1, 0, width - 1, height - 1, 0, height - 1]))
    const transform = own(cv.getPerspectiveTransform(from, to))
    const dst = own(new cv.Mat())
    cv.warpPerspective(src, dst, transform, new cv.Size(width, height), cv.INTER_LINEAR, cv.BORDER_REPLICATE, new cv.Scalar())
    const image = outContext.createImageData(width, height)
    image.data.set(dst.data)
    outContext.putImageData(image, 0, 0)
    return out
  })
}

interface Size {
  width: number
  height: number
}

// assumes the video stream is a center crop of the still's field of view (cover-fit); equal aspects reduce to plain scaling
export function mapVideoPointToStill(p: Pt, video: Size, still: Size): Pt {
  const scale = Math.max(video.width / still.width, video.height / still.height)
  return {
    x: p.x / scale + (still.width - video.width / scale) / 2,
    y: p.y / scale + (still.height - video.height / scale) / 2,
  }
}

// true when every corner moved at most `px` between the two quads
export function isStableQuad(prev: Pt[] | null, next: Pt[] | null, px: number): boolean {
  if (!prev || !next || prev.length !== next.length) return false
  return prev.every((p, i) => distance(p, next[i]) <= px)
}
